import {Outlet} from '@github-ui/react-router'
import {pullRequestsLayoutRoute} from './layout-route'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useRespondToRouteChange} from '../hooks/use-respond-to-route-change'
import {type PropsWithChildren, type ReactNode, useCallback, useRef} from 'react'
import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {useEnabledDockedPanelIds} from '../components/toolbar/panel-registry'
import {DockedPanelProvider} from '../contexts/DockedPanelContext'
import {
  condensedResponsiveWrapperClasses,
  fullWidthResponsiveWrapperClasses,
} from '../components/StickyPullRequestHeader'
import {useActiveRoute} from '../hooks/use-active-route'
import {getRouteConfig} from './route-manager'
import {DiffComparisonViewer} from '../components/DiffComparisonViewer'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {PullRequestHeader} from '../components/PullRequestHeader'
import {PullRequestMiniHeader} from '../components/PullRequestMiniHeader'
import type {AliveChannels, HeaderPageData} from '../page-data/payloads/header'
import {useRefetchOnAliveUpdate, useRespondToAliveUpdate} from '../hooks/use-refetch-on-alive-update'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {LayoutStoreProvider} from '../stores/LayoutStore'
import {FilesChangedDataCapture, LayoutStoreSeeder} from '../components/LayoutStoreSeeder'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import {useHeaderLiveUpdates} from '../hooks/use-header-live-updates'
import {useDescriptionLiveUpdates} from '../hooks/use-description-live-updates'
import {useFooterBorder} from '../hooks/use-footer-border'
import {MergeMethodContextProvider} from '@github-ui/mergebox'
import {useMergeStatusButtonData} from '../hooks/use-merge-status-button-data'

export function LayoutEntrypoint() {
  const {
    data: {
      pullRequest,
      bannersData,
      featureStates,
      repository,
      urls,
      user,
      aliveChannels,
      pageTitle,
      stack,
      mergeStatusButtonData,
      viewSettings,
      viewerPendingReview,
    },
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {refetch: refetchTabCounts} = useRouteQuery(pullRequestsLayoutRoute, 'tabCounts')
  const {refetch: refetchDiffstat} = useRouteQuery(pullRequestsLayoutRoute, 'diffstat')
  const needUpdateRef = useRef(false)

  const markNeedsUpdate = useCallback(() => {
    needUpdateRef.current = true
  }, [])

  useRespondToAliveUpdate(aliveChannels.pullRequest, markNeedsUpdate, {git_updated: true, timeline_updated: true})

  useRefetchOnAliveUpdate(aliveChannels.pullRequest, refetchTabCounts, {tab_counts_updated: true})

  // Refetch shared data when the route changes to keep diffstat and tab counts up to date
  // we only refetch on navigation to avoid excessive refetching.
  const handleRouteChange = useCallback(() => {
    if (!needUpdateRef.current) return

    needUpdateRef.current = false
    refetchTabCounts()
    refetchDiffstat()
  }, [refetchTabCounts, refetchDiffstat])

  useRespondToRouteChange(handleRouteChange)

  return (
    <LayoutComponent
      aliveChannels={aliveChannels}
      pullRequest={pullRequest}
      bannersData={bannersData}
      featureStates={featureStates}
      repository={repository}
      pageTitle={pageTitle}
      urls={urls}
      user={user}
      mergeStatusButtonData={mergeStatusButtonData}
      stack={stack}
      viewSettings={viewSettings}
      viewerPendingReview={viewerPendingReview}
    >
      <Outlet />
    </LayoutComponent>
  )
}

function HeaderLiveUpdates({aliveChannels}: {aliveChannels: AliveChannels}) {
  useHeaderLiveUpdates(aliveChannels)
  return null
}

// Subscribes once for the whole layout so multiple `useDescription` consumers
// (overview panel + toolbar button) share a single alive handler and trigger
// at most one refetch per real change. Must render under PageDataContextProvider.
function DescriptionLiveUpdates({aliveChannels}: {aliveChannels: AliveChannels}) {
  useDescriptionLiveUpdates(aliveChannels.pullRequest)
  return null
}

export function LayoutComponent({
  aliveChannels,
  pullRequest,
  bannersData,
  featureStates,
  pageTitle,
  repository,
  stack,
  urls,
  user,
  children,
  mergeStatusButtonData,
  viewSettings,
  viewerPendingReview,
  /**  helpUrl is omitted to move away from prop drilling route, page, and query data
   * See: https://github.com/github/github-ui/blob/main/.github/instructions/pull-requests-overview.instructions.md */
}: PropsWithChildren<Omit<HeaderPageData, 'helpUrl'>>) {
  const activeRoute = useActiveRoute()
  const {layout, dockedPanelSupported} = getRouteConfig(activeRoute)
  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')
  const isFullWidth = layout === 'full-width'
  const responsiveWrapperClasses = isFullWidth ? fullWidthResponsiveWrapperClasses : condensedResponsiveWrapperClasses
  const enabledPanels = useEnabledDockedPanelIds()

  const basePath = pullRequest.pathName ?? urls.conversation
  const initialFilesDataRef = useRef<FilesRoutePayload | undefined>(undefined)
  const seededRef = useRef(false)

  // Hoisted MergeMethodContextProvider so the in-header merge status trigger
  // and the centrally-rendered MergeStatusPanel share a single mergeMethod
  // state. The provider must live inside `PageDataContextProvider` because
  // its invalidation effect depends on `useMergeBoxPageDataQueryKey`. We key
  // the provider on the PR id so a soft navigation between PRs resets the
  // selected merge method instead of carrying it over.
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const wrapInMergeMethodContext = (node: ReactNode) =>
    validatedMergeData ? (
      <MergeMethodContextProvider key={pullRequest.id} defaultMergeMethod={validatedMergeData.defaultMergeMethod}>
        {node}
      </MergeMethodContextProvider>
    ) : (
      <>{node}</>
    )

  useFooterBorder(showUpdatedHeader)

  return (
    <>
      <FilesChangedDataCapture dataRef={initialFilesDataRef} />
      <LayoutStoreProvider
        initData={{pullRequest, repository, user, viewerPendingReview}}
        basePath={basePath}
        initialFilesDataRef={initialFilesDataRef}
        seededRef={seededRef}
      >
        <LayoutStoreSeeder seededRef={seededRef} />
        <DockedPanelProvider isOnSupportedRoute={dockedPanelSupported} enabledPanels={enabledPanels}>
          <PageDataContextProvider basePageDataUrl={urls.conversation}>
            {wrapInMergeMethodContext(
              <UserNameDisplayContextProvider value={repository.userNameDisplayConfiguration || undefined}>
                <DiffComparisonViewer.Container
                  className={showUpdatedHeader ? responsiveWrapperClasses : `tmp-mt-4 ${responsiveWrapperClasses}`}
                  dockedPanelWidth={viewSettings.dockedPanelWidth}
                  owner={repository.ownerLogin}
                  repo={repository.name}
                  pullNumber={pullRequest.number}
                  stickyHeader={
                    showUpdatedHeader ? (
                      <PullRequestMiniHeader
                        aliveChannels={aliveChannels}
                        bannersData={bannersData}
                        featureStates={featureStates}
                        pageTitle={pageTitle}
                        activeRoute={activeRoute}
                        repository={repository}
                        pullRequest={pullRequest}
                        stack={stack}
                        urls={urls}
                        user={user}
                        mergeStatusButtonData={mergeStatusButtonData}
                        viewSettings={viewSettings}
                      />
                    ) : undefined
                  }
                >
                  <DiffComparisonViewer.Header divider="none" padding="none">
                    <PullRequestHeader
                      aliveChannels={aliveChannels}
                      bannersData={bannersData}
                      featureStates={featureStates}
                      pageTitle={pageTitle}
                      activeRoute={activeRoute}
                      stickyHeaderVariant={layout}
                      repository={repository}
                      pullRequest={pullRequest}
                      stack={stack}
                      urls={urls}
                      user={user}
                      mergeStatusButtonData={mergeStatusButtonData}
                      viewSettings={viewSettings}
                    />
                  </DiffComparisonViewer.Header>
                  {children}
                </DiffComparisonViewer.Container>
                <HeaderLiveUpdates aliveChannels={aliveChannels} />
                <DescriptionLiveUpdates aliveChannels={aliveChannels} />
              </UserNameDisplayContextProvider>,
            )}
          </PageDataContextProvider>
        </DockedPanelProvider>
      </LayoutStoreProvider>
    </>
  )
}
